/**
 * TenantOrganizationSection — Issue #268.
 *
 * Renders the first-stage tenant & organization governance entry inside the
 * `/settings` page. The section is READ-ONLY:
 *   - Current tenant + primary organization (single-tenant locked).
 *   - Recommended departments / teams / channel categories.
 *   - First-version governance boundaries (no online tenant creation, no
 *     cross-tenant admin, no independent finance / inventory / lab per org,
 *     no physical deletion).
 *   - Initialization audit events (non-PII summary).
 *
 * Red lines:
 *   - No fake form, no optimistic editing. All data comes from the typed
 *     `@jinzhuan/api-client`.
 *   - We never render employee PII (phone, email, ID card, salary, contract)
 *     because the backend never returns it on these endpoints.
 *   - The "tenant management" missing-contract placeholder is removed; this
 *     section is the real entry, with explicit copy explaining the boundary.
 */
import { useMemo, type ReactElement } from 'react';
import { Building2, Layers, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useTenantOrganizationView,
  type AsyncState,
} from '@/hooks/useTenantOrganization';
import type { OrganizationUnitDto } from '@jinzhuan/api-client';

export const TENANT_ORGANIZATION_READ_PERMISSION = 'tenant_organization:read';
const SUPER_ADMIN = '*:*';

export function canSeeTenantOrganizationSection(
  permissions: readonly string[],
): boolean {
  return (
    permissions.includes(SUPER_ADMIN) ||
    permissions.includes(TENANT_ORGANIZATION_READ_PERMISSION)
  );
}

const UNIT_TYPE_LABEL: Record<string, string> = {
  department: '部门',
  team: '团队',
  channel: '渠道分类',
};

export interface TenantOrganizationSectionProps {
  readonly permissions: readonly string[];
}

/**
 * Permission gate. When the caller lacks
 * `tenant_organization:read` / `*:*`, we render the denial copy WITHOUT
 * mounting {@link AuthorizedTenantOrganizationSection}. That child component
 * owns the call to {@link useTenantOrganizationView}, so the typed
 * `@jinzhuan/api-client` GET requests never fire for unauthorized accounts.
 */
export default function TenantOrganizationSection({
  permissions,
}: TenantOrganizationSectionProps): ReactElement | null {
  const canRead = canSeeTenantOrganizationSection(permissions);

  if (!canRead) {
    return (
      <section
        className="space-y-3"
        data-testid="settings-tenant-organization-section"
        aria-label="租户与机构管理"
      >
        <header>
          <h3 className="text-lg font-medium">租户与机构管理</h3>
          <p className="text-sm text-muted-foreground">
            当前账号未获得 <code>tenant_organization:read</code> 权限；请联系超级管理员授权后查看。
          </p>
        </header>
      </section>
    );
  }

  return <AuthorizedTenantOrganizationSection />;
}

/**
 * Authorized body. Splitting the hook into a child component means React
 * never instantiates {@link useTenantOrganizationView} for unauthorized
 * accounts — protected GET endpoints are never called on their behalf.
 */
function AuthorizedTenantOrganizationSection(): ReactElement {
  const view = useTenantOrganizationView();
  const groupedUnits = useMemo(() => groupUnitsByType(view.units), [view.units]);

  return (
    <section
      className="space-y-3"
      data-testid="settings-tenant-organization-section"
      aria-label="租户与机构管理"
    >
      <header>
        <h3 className="text-lg font-medium">租户与机构管理</h3>
        <p className="text-sm text-muted-foreground">
          第一版仅一个租户与一个主机构，且不开放在线新增 / 编辑租户或跨租户管理员。
          后端契约真实读取；本页不写入任何假状态。
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" aria-hidden />
            当前租户与主机构
          </CardTitle>
          <CardDescription>
            来自 GET <code>/api/v1/settings/tenant-organization/current</code>。
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderCurrent(view.current)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4" aria-hidden />
            组织单元
          </CardTitle>
          <CardDescription>
            部门 / 团队 / 渠道分类的只读结构。来自 GET{' '}
            <code>/api/v1/settings/tenant-organization/units</code>。
          </CardDescription>
        </CardHeader>
        <CardContent>{renderUnits(view.units, groupedUnits)}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="h-4 w-4" aria-hidden />
            治理边界
          </CardTitle>
          <CardDescription>
            第一版锁定的治理边界；UI 仅展示，不支持开启。来自 GET{' '}
            <code>/api/v1/settings/tenant-organization/governance-boundaries</code>。
          </CardDescription>
        </CardHeader>
        <CardContent>{renderGovernance(view.governance)}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">初始化审计事件</CardTitle>
          <CardDescription>
            来自 GET <code>/api/v1/settings/tenant-organization/audit-events</code>。
            仅展示非敏感结构字段；不包含员工 PII。
          </CardDescription>
        </CardHeader>
        <CardContent>{renderAuditEvents(view.auditEvents)}</CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void view.refresh();
          }}
          data-testid="settings-tenant-organization-refresh"
        >
          刷新
        </Button>
      </div>
    </section>
  );
}

function renderCurrent(
  state: AsyncState<NonNullable<ReturnType<typeof useTenantOrganizationView>['current']>['data']>,
): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-current-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        data-testid="settings-tenant-organization-current-error"
        role="alert"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data || !state.data.initialized) {
    const fallback =
      '当前会话尚未关联租户与主机构；请联系超级管理员通过受控初始化流程完成接入。';
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-current-empty"
        data-uninitialized-reason={state.data?.reason ?? 'tenant_missing'}
      >
        {state.data?.message ?? fallback}
      </div>
    );
  }
  const tenant = state.data.tenant;
  const org = state.data.primaryOrganization;
  if (!tenant || !org) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-current-empty"
        data-uninitialized-reason={state.data.reason ?? 'tenant_missing'}
      >
        {state.data.message ??
          '当前会话尚未关联租户与主机构；请联系超级管理员通过受控初始化流程完成接入。'}
      </div>
    );
  }
  return (
    <div className="grid gap-4 text-sm md:grid-cols-2" data-testid="settings-tenant-organization-current">
      <dl className="space-y-2">
        <h4 className="text-xs font-medium uppercase text-muted-foreground">租户</h4>
        <Field label="编码" value={tenant.code} testId="settings-tenant-code" />
        <Field label="名称" value={tenant.name} testId="settings-tenant-name" />
        <Field label="状态" value={tenant.status} testId="settings-tenant-status" />
        <Field label="模式" value={tenant.mode} testId="settings-tenant-mode" />
        <Field
          label="官网主域名"
          value={tenant.primaryDomain ?? '—'}
          testId="settings-tenant-primary-domain"
        />
        <Field
          label="管理后台入口"
          value={tenant.adminEntryDomain ?? '—'}
          testId="settings-tenant-admin-entry-domain"
        />
        <Field
          label="保留策略说明"
          value={tenant.retentionPolicyNote ?? '—'}
          testId="settings-tenant-retention-policy"
        />
        <Field
          label="备份周期说明"
          value={tenant.backupScheduleNote ?? '—'}
          testId="settings-tenant-backup-schedule"
        />
      </dl>
      <dl className="space-y-2">
        <h4 className="text-xs font-medium uppercase text-muted-foreground">主机构</h4>
        <Field label="编码" value={org.code ?? '—'} testId="settings-primary-org-code" />
        <Field label="名称" value={org.name} testId="settings-primary-org-name" />
        <Field label="状态" value={org.status} testId="settings-primary-org-status" />
        <Field
          label="是否主机构"
          value={org.isPrimary ? '是' : '否'}
          testId="settings-primary-org-is-primary"
        />
        <Field
          label="独立财务"
          value={org.financeIsolated ? '是' : '否'}
          testId="settings-primary-org-finance-isolated"
        />
        <Field
          label="独立库存"
          value={org.inventoryIsolated ? '是' : '否'}
          testId="settings-primary-org-inventory-isolated"
        />
        <Field
          label="独立实验室"
          value={org.labIsolated ? '是' : '否'}
          testId="settings-primary-org-lab-isolated"
        />
      </dl>
    </div>
  );
}

function renderUnits(
  state: AsyncState<readonly OrganizationUnitDto[]>,
  groups: ReadonlyMap<string, readonly OrganizationUnitDto[]>,
): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-units-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        data-testid="settings-tenant-organization-units-error"
        role="alert"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data || state.data.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-units-empty"
      >
        当前 tenant/org 尚无组织单元；初始化 seed 将在首次访问时写入。
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="settings-tenant-organization-units">
      {[...groups.entries()].map(([unitType, list]) => (
        <div key={unitType} data-testid={`settings-tenant-organization-unit-group-${unitType}`}>
          <h4 className="mb-2 text-xs font-medium uppercase text-muted-foreground">
            {UNIT_TYPE_LABEL[unitType] ?? unitType}
            <span className="ml-2 text-muted-foreground">（{list.length}）</span>
          </h4>
          <ul className="grid gap-1 text-xs md:grid-cols-2 lg:grid-cols-3">
            {list.map((unit) => (
              <li
                key={unit.id}
                className="rounded border bg-muted/40 px-2 py-1"
                data-testid={`settings-tenant-organization-unit-${unit.code}`}
              >
                <span className="font-mono text-xs text-muted-foreground">{unit.code}</span>
                <span className="ml-2">{unit.name}</span>
                {unit.status !== 'active' && (
                  <span className="ml-2 text-xs text-muted-foreground">({unit.status})</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function renderGovernance(
  state: AsyncState<ReturnType<typeof useTenantOrganizationView>['governance']['data']>,
): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-governance-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        data-testid="settings-tenant-organization-governance-error"
        role="alert"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-governance-empty"
      >
        未读取到治理边界配置。
      </div>
    );
  }
  const flags: ReadonlyArray<{ label: string; value: boolean; testId: string }> = [
    {
      label: '在线新增租户',
      value: state.data.allowOnlineTenantCreation,
      testId: 'settings-governance-allow-online-tenant-create',
    },
    {
      label: '在线编辑租户',
      value: state.data.allowOnlineTenantEditing,
      testId: 'settings-governance-allow-online-tenant-edit',
    },
    {
      label: '跨租户管理员',
      value: state.data.allowCrossTenantAdministration,
      testId: 'settings-governance-allow-cross-tenant-admin',
    },
    {
      label: '跨租户迁移',
      value: state.data.allowCrossTenantMigration,
      testId: 'settings-governance-allow-cross-tenant-migration',
    },
    {
      label: '机构独立财务',
      value: state.data.allowIndependentFinancePerOrg,
      testId: 'settings-governance-allow-independent-finance',
    },
    {
      label: '机构独立库存',
      value: state.data.allowIndependentInventoryPerOrg,
      testId: 'settings-governance-allow-independent-inventory',
    },
    {
      label: '机构独立实验室',
      value: state.data.allowIndependentLabPerOrg,
      testId: 'settings-governance-allow-independent-lab',
    },
    {
      label: '物理删除数据',
      value: state.data.allowPhysicalDeletion,
      testId: 'settings-governance-allow-physical-deletion',
    },
  ];
  return (
    <div className="space-y-3" data-testid="settings-tenant-organization-governance">
      <ul className="grid gap-2 text-sm md:grid-cols-2">
        {flags.map((flag) => (
          <li
            key={flag.testId}
            className="flex items-center justify-between rounded border bg-muted/30 px-2 py-1"
            data-testid={flag.testId}
          >
            <span>{flag.label}</span>
            <span className={flag.value ? 'text-emerald-600' : 'text-muted-foreground'}>
              {flag.value ? '允许' : '禁止'}
            </span>
          </li>
        ))}
      </ul>
      <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
        {state.data.notes.map((note, idx) => (
          <li
            key={`note-${idx}-${note.slice(0, 16)}`}
            data-testid={`settings-governance-note-${idx}`}
          >
            {note}
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderAuditEvents(
  state: AsyncState<ReturnType<typeof useTenantOrganizationView>['auditEvents']['data']>,
): ReactElement {
  if (state.loading && !state.data) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-audit-events-loading"
      >
        正在加载…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="text-sm text-destructive"
        data-testid="settings-tenant-organization-audit-events-error"
        role="alert"
      >
        {state.error}
      </div>
    );
  }
  if (!state.data || state.data.length === 0) {
    return (
      <div
        className="text-sm text-muted-foreground"
        data-testid="settings-tenant-organization-audit-events-empty"
      >
        暂无初始化审计事件。
      </div>
    );
  }
  return (
    <ul
      className="space-y-2 text-xs"
      data-testid="settings-tenant-organization-audit-events"
    >
      {state.data.map((event) => (
        <li
          key={event.id}
          className="rounded border p-2"
          data-testid={`settings-tenant-organization-audit-event-${event.id}`}
        >
          <div className="flex items-center justify-between">
            <span className="font-mono">{event.action}</span>
            <span className="text-muted-foreground">
              {new Date(event.createdAt).toLocaleString()}
            </span>
          </div>
          <div className="mt-1 text-muted-foreground">
            目标：{event.targetType}
            {event.targetId ? `（${event.targetId}）` : ''}；操作人：{event.userId ?? 'seed'}；
            风险：{event.riskLevel}；状态：{event.status}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Field({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}): ReactElement {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2">
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className="text-sm" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

function groupUnitsByType(
  state: AsyncState<readonly OrganizationUnitDto[]>,
): ReadonlyMap<string, readonly OrganizationUnitDto[]> {
  const grouped = new Map<string, OrganizationUnitDto[]>();
  if (!state.data) return grouped;
  for (const row of state.data) {
    const key = row.unitType;
    const list = grouped.get(key);
    if (list) {
      list.push(row);
    } else {
      grouped.set(key, [row]);
    }
  }
  return grouped;
}
